// Progress of one Revert VM run: status banner and steps table. See revertRuns.spec.md (RVR-FR-007)
// See RevertRunTracker.spec.md for RRT-FR-001, RRT-FR-002, RRT-FR-003
import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import InfoPanel from 'core/components/InfoPanel'
import Banner from 'core/elements/banner/Banner'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import { getRevertStepStatus, getRunOutcome, type RevertRun, type StepStatus } from './revertRuns'

const STEP_STATUS_DISPLAY: Record<StepStatus, { label: string; icon?: string; color: string }> = {
  waiting: { label: 'Waiting', color: 'grey' },
  inProgress: { label: 'In progress', icon: 'arrows-rotate', color: 'blue' },
  completed: { label: 'Completed', icon: 'check', color: 'green' },
  failed: { label: 'Failed', icon: 'circle-exclamation', color: 'red' },
}

const StepStatusIcon = ({ status }: { status: StepStatus }) => {
  const classes = useStyles()
  const { icon, color } = STEP_STATUS_DISPLAY[status]
  return icon ? (
    <FontAwesomeIcon className={classes[`stepIcon_${color}`]} spin={status === 'inProgress'}>
      {icon}
    </FontAwesomeIcon>
  ) : (
    <span className={classes.stepDot} />
  )
}

const StepStatusLabel = ({ status }: { status: StepStatus }) => {
  const classes = useStyles()
  return (
    <div className={classes.stepStatus}>
      <StepStatusIcon status={status} />
      <Text variant="body2" component="span">
        {STEP_STATUS_DISPLAY[status].label}
      </Text>
    </div>
  )
}

export default function RevertRunTracker({ run }: { run: RevertRun }) {
  const classes = useStyles()
  const outcome = getRunOutcome(run)
  const hasFailed = outcome === 'failed' || outcome === 'attention'
  const isComplete = outcome === 'success'
  // The volume reverts are started by the browser once the rebuild is done
  const awaitingVolumeReverts =
    !hasFailed && run.volumes.length > 0 && run.rebuildStatus !== 'completed'

  const steps = [
    { id: 'rebuild', name: 'Rebuild boot disk', status: run.rebuildStatus, volumes: [] },
    ...(run.volumes.length
      ? [
          {
            id: 'revert',
            name: 'Revert data volumes',
            status: getRevertStepStatus(run),
            volumes: run.volumes,
          },
        ]
      : []),
  ]

  return (
    <div className={classes.container}>
      <Banner
        icon={hasFailed ? 'circle-exclamation' : isComplete ? 'check' : 'arrows-rotate'}
        variant={hasFailed ? 'error' : 'informative'}
        title={
          hasFailed ? 'Revert failed' : isComplete ? 'Revert complete' : 'Reverting in progress…'
        }
      >
        <Text variant="body2">
          {run.error ?? `Reverting to ${run.snapshotName}.`}
          {!hasFailed &&
            !isComplete &&
            ' You can close this drawer — progress is shown in the task tray at the bottom right.'}
        </Text>
      </Banner>
      {awaitingVolumeReverts && (
        <Banner
          icon="triangle-exclamation"
          variant="warning"
          title="Keep this browser tab open until the rebuild finishes"
        >
          <Text variant="body2">
            The volume reverts start only after the rebuild completes. If you close or refresh this
            browser tab before then, the volume reverts will not happen and you will have to go
            through the Revert to Snapshot flow again.
          </Text>
        </Banner>
      )}
      <InfoPanel title={`Steps (${steps.length})`} dataTestId="revert-steps">
        <div>
          <div className={classes.stepRow}>
            <Text variant="caption1" className={classes.muted}>
              Status
            </Text>
            <Text variant="caption1" className={classes.muted}>
              Step Name
            </Text>
          </div>
          {steps.map((step) => (
            <div key={step.id} data-testid={`revert-step-${step.id}`}>
              <div className={classes.stepRow}>
                <StepStatusLabel status={step.status} />
                <Text variant="body2">{step.name}</Text>
              </div>
              {step.volumes.map((volume) => (
                <div className={classes.stepVolume} key={volume.id}>
                  <StepStatusIcon status={volume.status} />
                  <Text variant="caption1">
                    <b>{volume.name}</b>
                  </Text>
                </div>
              ))}
            </div>
          ))}
        </div>
      </InfoPanel>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  stepRow: {
    display: 'grid',
    gridTemplateColumns: '150px 1fr',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  stepStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  stepVolume: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 1, 5),
    background: theme.palette.grey['50'],
  },
  stepDot: {
    width: 8,
    height: 8,
    margin: '0 4px',
    borderRadius: '50%',
    background: theme.palette.grey[300],
  },
  stepIcon_grey: { color: theme.palette.grey[500] },
  stepIcon_blue: { color: theme.palette.blue[500] },
  stepIcon_green: { color: theme.palette.green[500] },
  stepIcon_red: { color: theme.palette.red[900] },
  muted: {
    color: theme.palette.grey[500],
  },
}))
